/**
 * The one search field: hero on Now, compact on the stage. Suggestions come from the data model (field
 * names), the data (values of a field once "Field=" is typed, the cloud's `suggest` for anything else)
 * and what was searched before, grouped by what they are. Arrow keys pick, Enter applies or searches,
 * Esc closes, ⌘K focuses from anywhere. The syntax (Field=value, @status=) is never required.
 */
import { SearchHint } from '../components/SearchHint.tsx';
import { SearchTips } from '../components/SearchTips.tsx';
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useAuth } from '../lib/auth.tsx';
import { Api, type ModelSnapshot, type SuggestGroup } from '../lib/api.ts';
import { recentSearches } from '../lib/hooks.ts';
import { count, humanize, quote } from '../lib/format.ts';
import { registeredSystems, stripSystem, systemName, systemOf } from '../lib/systems.ts';
import { integrationPath, systemPath } from '../lib/systemsview.ts';
import { useNavigate } from 'react-router';
import { useEnv } from '../lib/envs.tsx';
import { ClockIcon, CloseIcon, HelpIcon, SearchIcon } from '../components/icons.tsx';
import { IconButton, Kicker, SystemBadge, cx, noun } from '../design/primitives.tsx';

interface Item {
  id: string;
  /** What is shown: an identifier (mono) or a name. */
  label: string;
  mono?: boolean;
  detail?: string;
  count?: number;
  recent?: boolean;
  apply: (q: string) => string;
  /** Picking it searches at once (a thing) instead of leaving the cursor in the field (a field to fill). */
  submit: boolean;
  /** Picking it opens this page instead (a system, an integration): a history step. */
  go?: string;
  /** A system's badge before the label. */
  badge?: string;
}
interface Group { key: string; label: string; items: Item[] }

const META: [string, string][] = [
  ['@status=', 'SUCCESS, FAILED, HOLD, NEW, PROCESSING'], ['@class=', 'data model'], ['@process:', 'word in a process name'],
  ['@flag=parseError', 'payload could not be parsed'], ['@typeError=', 'value did not match the field type'],
];
const STATUSES = ['FAILED', 'SUCCESS', 'HOLD', 'NEW', 'PROCESSING'];
const PER_GROUP = 6;
const SUGGEST_FROM = 'now-24h';

let modelCache: { tenant: string; at: number; model: ModelSnapshot } | null = null;
const suggestCache = new Map<string, SuggestGroup[]>();

/** Field leaf names across classes: name → classes and total count. */
function fieldIndex(model: ModelSnapshot | undefined) {
  const map = new Map<string, { classes: Set<string>; count: number }>();
  for (const c of model?.classes ?? []) {
    for (const f of c.fields) {
      const e = map.get(f.leaf) ?? { classes: new Set(), count: 0 };
      e.classes.add(c.name);
      e.count += f.count;
      map.set(f.leaf, e);
    }
  }
  return [...map.entries()].map(([name, e]) => ({ name, classes: [...e.classes], count: e.count }));
}

/** Replaces the last (partial) term of the query. */
function replaceLast(q: string, term: string): string {
  const i = q.search(/\S+$/);
  return (i < 0 ? q : q.slice(0, i)) + term;
}

const looksLikeClass = (s: string) => /^[A-Z][A-Za-z0-9]*[a-z][A-Za-z0-9]*[A-Z]/.test(s);
const looksLikeField = (s: string) => /^[A-Za-z][A-Za-z0-9_]*$/.test(s) && !/^\+\d+$/.test(s);
/** "ORDER_NO · ErpSalesOrder, WebOrder +2" → "Order no · sales orders, orders +2": names in a detail become words. */
function humanDetail(detail: string | null | undefined): string | undefined {
  if (!detail) return undefined;
  return detail.split(/(,\s*|\s·\s|\s)/)
    .map((part) => (looksLikeClass(part) ? noun(part, 2) : looksLikeField(part) ? humanize(/^[A-Z0-9_]+$/.test(part) ? part.toLowerCase() : part) : part))
    .join('');
}
/** The value or one of its words starts with the prefix (as the cloud's suggest matches). */
const wordStarts = (value: string, p: string) => {
  const v = value.toLowerCase();
  return v.startsWith(p) || v.split(/[^\p{L}\p{N}]+/u).some((w) => w !== '' && w.startsWith(p));
};
const PER_THING = 4;

/** The systems of the registry whose code, name or alias starts with the prefix (or a word of it), hidden ones left out. */
function systemItems(p: string): Item[] {
  return [...registeredSystems().values()]
    .filter((s) => s.status !== 'hidden' && [s.code, s.name, ...s.aliases].some((v) => wordStarts(v, p)))
    .sort((a, b) => Number(b.code.toLowerCase() === p) - Number(a.code.toLowerCase() === p) || a.name.localeCompare(b.name))
    .slice(0, PER_THING)
    .map((s): Item => ({ id: `system:${s.code}`, label: s.name, detail: s.code === s.name ? 'system' : `${s.code} · system`, badge: s.code, apply: (x) => x, submit: false, go: systemPath(s.code) }));
}

const classList = (classes: string[]) => `${classes.slice(0, 2).map((c) => noun(c, 2)).join(', ')}${classes.length > 2 ? ` +${classes.length - 2}` : ''}`;

export function SearchField({ value, onSubmit, size = 'compact', autoFocus, className }: {
  value: string; onSubmit: (q: string) => void; size?: 'hero' | 'compact'; autoFocus?: boolean; className?: string;
}) {
  const { tenant } = useAuth();
  const navigate = useNavigate();
  const { link } = useEnv();
  const [q, setQ] = useState(value);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [tips, setTips] = useState(false);
  const [model, setModel] = useState<ModelSnapshot>();
  const [values, setValues] = useState<{ field: string; items: { value: string; count: number }[] }>();
  const [remote, setRemote] = useState<{ prefix: string; groups: SuggestGroup[] }>();
  const input = useRef<HTMLInputElement>(null);
  const seq = useRef(0);
  const listId = useRef(`mv-suggest-${Math.random().toString(36).slice(2, 8)}`).current;
  const hero = size === 'hero';

  useEffect(() => setQ(value), [value]);

  useEffect(() => {
    if (!tenant) return;
    if (modelCache && modelCache.tenant === tenant.id && Date.now() - modelCache.at < 60_000) {
      setModel(modelCache.model);
      return;
    }
    Api.model(tenant.id).then((m) => {
      modelCache = { tenant: tenant.id, at: Date.now(), model: m };
      setModel(m);
    }).catch(() => undefined);
  }, [tenant]);

  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        input.current?.focus();
        input.current?.select();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const last = /\S+$/.exec(q)?.[0] ?? '';
  const valueTerm = /^(-?)([^@=:<>\s]+)=([^"]*)$/.exec(last);
  const valueField = valueTerm?.[2];
  // A plain word of two or more characters: what the cloud suggests values for
  const freeTerm = !valueTerm && !last.startsWith('@') && !/[=:<>]/.test(last) && last.replace(/^-/, '').length >= 2 ? last.replace(/^-/, '') : '';

  // Most common values of a field once "Field=" is typed (debounced)
  useEffect(() => {
    if (!tenant || !valueField) return;
    if (values && values.field === valueField) return;
    const t = setTimeout(() => {
      Api.search(tenant.id, new URLSearchParams({ q: '', size: '0', facet: valueField, facetSize: '40', from: 'now-30d' }))
        .then((r) => {
          const merged = new Map<string, number>();
          for (const [path, buckets] of Object.entries(r.fieldFacets)) {
            if (path !== valueField && !path.endsWith('/' + valueField)) continue;
            for (const b of buckets) merged.set(b.value, (merged.get(b.value) ?? 0) + b.count);
          }
          setValues({ field: valueField, items: [...merged.entries()].sort((a, b) => b[1] - a[1]).map(([v, n]) => ({ value: v, count: n })) });
        }).catch(() => undefined);
    }, 200);
    return () => clearTimeout(t);
  }, [tenant, valueField]); // eslint-disable-line react-hooks/exhaustive-deps

  // What the data has that starts with the word being typed (debounced 150 ms, newest answer wins)
  useEffect(() => {
    if (!tenant || !freeTerm) return;
    const key = `${tenant.id}:${freeTerm.toLowerCase()}`;
    const cached = suggestCache.get(key);
    if (cached) {
      setRemote({ prefix: freeTerm, groups: cached });
      return;
    }
    const t = setTimeout(() => {
      const mine = ++seq.current;
      Api.suggest(tenant.id, freeTerm, SUGGEST_FROM).then((r) => {
        suggestCache.set(key, r.groups);
        if (mine === seq.current) setRemote({ prefix: freeTerm, groups: r.groups });
      }).catch(() => undefined);
    }, 150);
    return () => clearTimeout(t);
  }, [tenant, freeTerm]);

  const fields = useMemo(() => fieldIndex(model), [model]);

  const groups: Group[] = useMemo(() => {
    if (!q.trim()) {
      const recent = recentSearches().slice(0, PER_GROUP).map((r): Item => ({ id: `recent:${r}`, label: r, mono: true, recent: true, apply: () => r, submit: true }));
      return recent.length ? [{ key: 'recent', label: 'Recent', items: recent }] : [];
    }
    if (last.startsWith('@status=') || last.startsWith('-@status=')) {
      const p = last.split('=')[1].toUpperCase();
      const neg = last.startsWith('-') ? '-' : '';
      const items = STATUSES.filter((s) => s.startsWith(p)).map((s): Item => ({
        id: `status:${s}`, label: `${neg}@status=${s}`, mono: true, apply: (x) => replaceLast(x, `${neg}@status=${s} `), submit: false,
      }));
      return items.length ? [{ key: 'filters', label: 'Status', items }] : [];
    }
    if (last.startsWith('@class=')) {
      const p = last.slice(7).toLowerCase();
      const items = (model?.classes ?? []).filter((c) => c.name.toLowerCase().includes(p) || noun(c.name, 2).includes(p)).slice(0, PER_GROUP)
        .map((c): Item => ({ id: `class:${c.name}`, label: noun(c.name, 2), detail: c.name, count: c.messages, apply: (x) => replaceLast(x, `@class=${c.name} `), submit: false }));
      return items.length ? [{ key: 'classes', label: 'Data models', items }] : [];
    }
    if (last.startsWith('@')) {
      const items = META.filter(([m]) => m.startsWith(last)).map(([m, d]): Item => ({ id: `meta:${m}`, label: m, mono: true, detail: d, apply: (x) => replaceLast(x, m), submit: false }));
      return items.length ? [{ key: 'filters', label: 'Filters', items }] : [];
    }
    if (valueTerm) {
      const [, neg, field, prefix] = valueTerm;
      const items = (values?.field === field ? values.items : []).filter((v) => v.value.toLowerCase().startsWith(prefix.toLowerCase())).slice(0, PER_GROUP)
        .map((v): Item => ({ id: `value:${v.value}`, label: v.value, mono: true, count: v.count, apply: (x) => replaceLast(x, `${neg}${field}=${quote(v.value)} `), submit: false }));
      return items.length ? [{ key: 'values', label: humanize(field), items }] : [];
    }
    if (!freeTerm) return [];
    const p = freeTerm.toLowerCase();
    const neg = last.startsWith('-') ? '-' : '';
    const out: Group[] = [];
    // The cloud's groups, narrowed to what is typed now while a newer answer is on its way
    const fromRemote = remote && p.startsWith(remote.prefix.toLowerCase()) ? remote.groups : [];
    const fieldItems = new Map<string, Item>();
    // Systems and integrations first: picking one opens its page (the systems from the registry, the integrations from the cloud)
    const systems = systemItems(p);
    if (systems.length) out.push({ key: 'systems', label: 'Systems', items: systems });
    const procs = fromRemote.filter((g) => g.kind === 'integration').flatMap((g) => g.items).filter((it) => wordStarts(it.value, p)).slice(0, PER_THING);
    if (procs.length) {
      out.push({
        key: 'integrations', label: 'Integrations',
        items: procs.map((it): Item => {
          const sys = systemOf(it.value);
          return { id: `integration:${it.value}`, label: stripSystem(it.value) || it.value, detail: sys ? `${systemName(sys)} · integration` : 'integration', badge: sys ?? undefined, count: it.count, apply: (x) => x, submit: false, go: integrationPath(it.value) };
        }),
      });
    }
    for (const g of fromRemote) {
      if (g.kind === 'system' || g.kind === 'integration') continue;
      const items = g.items.filter((it) => it.value.toLowerCase().startsWith(p));
      if (!items.length) continue;
      if (g.kind === 'field') {
        for (const it of items) {
          fieldItems.set(it.value, { id: `field:${it.value}`, label: humanize(it.value), detail: humanDetail(it.detail), count: it.count, apply: (x) => replaceLast(x, `${neg}${it.value}=`), submit: false });
        }
        continue;
      }
      out.push({
        key: `${g.kind}:${g.label}`, label: g.label,
        items: items.slice(0, PER_GROUP).map((it): Item => {
          if (g.kind === 'value') {
            // A process, data model, connection or status by name
            const term = it.detail === 'data model' ? `@class=${it.value} ` : it.detail === 'status' ? `@status=${it.value} ` : `${quote(it.value)} `;
            const label = it.detail === 'data model' ? noun(it.value, 2) : it.detail === 'process' ? stripSystem(it.value) : it.value;
            const sys = it.detail === 'process' ? systemOf(it.value) : null;
            return { id: `${g.kind}:${it.value}`, label, mono: it.detail === 'status', detail: sys ? `${it.detail} · ${sys}` : it.detail ?? undefined, count: it.count, apply: (x) => replaceLast(x, term), submit: true };
          }
          return { id: `${g.kind}:${it.value}`, label: it.value, mono: true, detail: humanDetail(it.detail), count: it.count, recent: g.kind === 'recent', apply: (x) => replaceLast(x, `${quote(it.value)} `), submit: true };
        }),
      });
    }
    // Field names from the model, merged with the cloud's
    for (const f of fields.filter((f) => f.name.toLowerCase().includes(p))
      .sort((a, b) => Number(b.name.toLowerCase().startsWith(p)) - Number(a.name.toLowerCase().startsWith(p)) || b.count - a.count)) {
      if (fieldItems.size >= PER_GROUP) break;
      if (!fieldItems.has(f.name)) {
        fieldItems.set(f.name, { id: `field:${f.name}`, label: humanize(f.name), detail: classList(f.classes), count: f.count, apply: (x) => replaceLast(x, `${neg}${f.name}=`), submit: false });
      }
    }
    if (fieldItems.size) out.push({ key: 'fields', label: 'Fields', items: [...fieldItems.values()].slice(0, PER_GROUP) });
    return out;
  }, [q, last, model, fields, values, valueTerm, freeTerm, remote]); // eslint-disable-line react-hooks/exhaustive-deps

  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups]);

  useEffect(() => setActive(-1), [q]);

  const submit = (text: string) => {
    const clean = text.replace(/[​-‍﻿]/g, '').replace(/ /g, ' ').trim();
    setOpen(false);
    setTips(false);
    onSubmit(clean);
  };

  const pick = (it: Item) => {
    if (it.go) {
      setOpen(false);
      setTips(false);
      void navigate(link(it.go));
      return;
    }
    const next = it.apply(q);
    setQ(next);
    if (it.submit) {
      submit(next);
    } else {
      input.current?.focus();
      setOpen(true);
    }
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown' && flat.length) {
      e.preventDefault();
      setOpen(true);
      setActive((a) => (a + 1) % flat.length);
    } else if (e.key === 'ArrowUp' && flat.length) {
      e.preventDefault();
      setActive((a) => (a <= 0 ? flat.length - 1 : a - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (open && active >= 0 && flat[active]) pick(flat[active]);
      else submit(q);
    } else if (e.key === 'Escape') {
      if (open) {
        e.preventDefault();
        setOpen(false);
      }
    }
  };

  const showPanel = open;
  let index = -1;

  return (
    <div className={cx('relative w-full', className)}>
      <div className={cx('flex items-center gap-3 rounded-full transition-shadow duration-[var(--t-std)] ease-mv',
        hero ? 'h-14 pl-5 pr-2 bg-glass backdrop-blur-md shadow-float' : 'h-10 pl-4 pr-1 bg-n2 shadow-node',
        open && 'outline-[1.5px] outline-accent')}>
        <SearchIcon size={hero ? 20 : 16} className="text-n8 shrink-0" />
        <label htmlFor={listId + '-input'} className="sr-only">Search</label>
        <input
          ref={input} id={listId + '-input'} autoFocus={autoFocus} autoComplete="off" spellCheck={false}
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={onKeyDown}
          placeholder="Search an order, a customer, an invoice, a run, a value"
          className={cx('flex-1 min-w-0 h-full bg-transparent border-0 outline-none focus-visible:outline-none font-mono text-n10 placeholder:font-sans placeholder:text-n7',
            hero ? 'text-body' : 'text-small')}
          role="combobox" aria-expanded={showPanel} aria-controls={listId} aria-autocomplete="list"
          aria-activedescendant={showPanel && active >= 0 ? `${listId}-${active}` : undefined}
        />
        {q ? (
          <IconButton label="Clear" onClick={() => { setQ(''); input.current?.focus(); setOpen(true); }} className={hero ? 'size-10' : 'size-8'}>
            <CloseIcon size={hero ? 18 : 15} />
          </IconButton>
        ) : <span className={hero ? 'w-3' : 'w-2'} />}
      </div>
      {!showPanel && q.trim() && (
        <SearchHint q={q} onFix={(next) => { setQ(next); submit(next); }} className="absolute left-4 right-4 top-full mt-2 z-30 shadow-node" />
      )}
      {showPanel && (
        <div id={listId} role="listbox"
          className="absolute left-0 right-0 top-full mt-2 z-40 bg-n2 rounded-card shadow-float p-2 max-h-[min(60vh,560px)] overflow-auto mv-pop">
          <div className="flex justify-end px-1 pb-0.5">
            <button type="button" aria-expanded={tips} onMouseDown={(e) => e.preventDefault()} onClick={() => setTips((v) => !v)}
              className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border-0 cursor-pointer text-small bg-transparent text-n8 hover:bg-n3 hover:text-n10 aria-expanded:bg-accent-soft aria-expanded:text-accent">
              <HelpIcon size={14} />{tips ? 'Hide search tips' : 'Search tips'}
            </button>
          </div>
          {tips && <SearchTips onPick={(example) => { setQ(example); input.current?.focus(); }} />}
          {!tips && groups.map((g) => (
            <div key={g.key} className="py-1">
              <Kicker className="px-3 pt-1.5 pb-1">{g.label}</Kicker>
              {g.items.map((it) => {
                const i = ++index;
                const on = i === active;
                return (
                  <div key={it.id} id={`${listId}-${i}`} role="option" aria-selected={on}
                    onMouseDown={(e) => { e.preventDefault(); pick(it); }}
                    onMouseMove={() => { if (!on) setActive(i); }}
                    className={cx('grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 h-9 px-3 rounded-chip cursor-pointer', on && 'bg-accent-soft')}>
                    <span className="flex items-baseline gap-3 min-w-0">
                      {it.recent && <ClockIcon size={13} className="text-n7 shrink-0 self-center" />}
                      {it.badge && <SystemBadge code={it.badge} size={20} className="self-center" />}
                      <span className={cx('truncate shrink-0 max-w-[60%]', it.mono ? 'font-mono text-small' : 'text-small font-medium', on ? 'text-accent' : 'text-n10')}>{it.label}</span>
                      {it.detail && <span className="truncate text-small text-n8">{it.detail}</span>}
                    </span>
                    {it.count !== undefined && <span className="font-mono text-micro text-n7 tabular-nums">{count(it.count)}</span>}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
